import React from 'react';
import Spinner from 'components/Spinner';
import PropTypes from 'prop-types';
import Component from 'BaseComponent';
import api from 'api';
import autobind from 'autobind-decorator';
import { notice, alert } from 'ducks/notifications';
import { invalidateRequests, query } from 'redux-bees';
import OperationButton from 'components/OperationButton';
import Time from 'components/Time';
import { Link, withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from 'react-intl';
import Tooltip from 'components/Tooltip';
import Pagination from 'components/Pagination';
import { connect } from 'react-redux';

const toRenderProp = ({ children, ...rest }) => children(rest);
const createQueryProvider = (...args) => query(...args)(toRenderProp);
const PaginationProvider = createQueryProvider('deploys', api.getDeploys, (perform, props) => (
  perform({ siteId: props.site.id, page: props.params.page })
));

@injectIntl

@withRouter
@connect()

export default class Deploys extends Component {
  static propTypes = {
    dispatch: PropTypes.func,
    site: PropTypes.object.isRequired,
    intl: intlShape.isRequired,
  }

  state = { page: '1' }

  @autobind
  handlePageChange(page) {
    this.setState({ page });
  }

  @autobind
  handleDeploy() {
    const { dispatch, site } = this.props;

    dispatch(api.triggerDeploy({ siteId: site.id }, null))
      .then(() => dispatch(invalidateRequests(api.getDeploys)))
      .then(() => dispatch(notice('Deploying')))
    .catch(({ body }) => {
      dispatch(alert(this.t(`error.${body.errors[0].code}`)));
    });
  }

  @autobind
  handleRestoreDeploy(id) {
    const { dispatch, site } = this.props;


    if (confirm('Are you sure?')) {
      return dispatch(api.restoreDeploy({ siteId: site.id, id }, null))
      .catch(({ body }) => dispatch(alert(this.t(`error.${body.errors[0].code}`))));
    }
    return true;
  }

  @autobind
  handleDownload(id) {
    const { dispatch, site } = this.props;
    return dispatch(api.requestDeployDownload({ siteId: site.id, id }, null))
    .catch(({ body }) => dispatch(alert(this.t(`error.${body.errors[0].code}`))));
  }

  @autobind
  handleCancelDeploy(id) {
    const { dispatch, site } = this.props;

    if (confirm('Are you sure?')) {
      return dispatch(api.cancelDeploy({ siteId: site.id, id }, null))
      .catch(() => dispatch(alert('Error')));
    }
    return true;
  }

  @autobind
  renderDeploy(deploy) {
    const { site, intl } = this.props;

    return (
      <div className="Deploys__item" key={deploy.id}>
        <Link
          key={deploy.id}
          to={`/site/${site.id}/deploys/${deploy.id}`}
          className="Deploys__item__cell Deploys__item__cell--state"
        >
          <div className={`Deploys__item__state is-${deploy.attributes.state}`}>
            { this.t(`deployState.${deploy.attributes.state}`) }
          </div>
        </Link>
        <div className="Deploys__item__cell Deploys__item__cell--info">
          {
            deploy.attributes.completedAt ?
              <div>
                <div className="Deploys__item__completed">
                  { (deploy.attributes.state === 'completed' || deploy.attributes.state === 'creating_backup') &&
                    <div>Finished <Time value={deploy.attributes.completedAt} /></div>
                  }
                  { deploy.attributes.state === 'failed' &&
                    <div>
                      <div className="Deploys__item__completed">
                        Started <Time value={deploy.attributes.createdAt} />
                      </div>
                    </div>
                  }

                </div>
                <div className="Deploys__item__time">
                  Deployed {
                    intl.formatRelative(
                      new Date(deploy.attributes.completedAt),
                      { now: new Date(deploy.attributes.createdAt) },
                    )
                  }
                  { deploy.attributes.state === 'creating_backup' &&
                    <span>
                      {' - Generating backup...'}
                    </span>
                  }
                </div>
              </div>
              :
              <div>
                <div className="Deploys__item__completed">
                  Started <Time value={deploy.attributes.createdAt} />
                </div>
              </div>
          }
        </div>
        <div className="Deploys__item__cell Deploys__item__cell--download">
          { deploy.relationships.download.data && deploy.attributes.downloadGeneratedAt &&
            <a className="button button--tiny button--primary" href={deploy.attributes.downloadUrl}>
              <i className="icon--download" />
            </a>
          }
          { deploy.relationships.download.data && !deploy.attributes.downloadGeneratedAt &&
            <Tooltip label="Archive is being generated...">
              <Spinner size={20} className="Spinner" />
            </Tooltip>
          }
          { (Number.parseInt(deploy.id, 10) === site.attributes.activeDeploy) &&
            deploy.attributes.state === 'completed' && site.attributes.wpState !== 'deploying' &&
            <div>
              { !deploy.relationships.download.data &&
                <Tooltip label="Generate a deploy archive. Depending on the size of the site, it may take several minutes">
                  <button
                    onClick={() => this.handleDownload(deploy.id)}
                    className="button button--tiny"
                  >
                    <i className="icon--download" />
                  </button>
                </Tooltip>
              }
            </div>
          }
        </div>
        { false && Number.parseInt(deploy.id, 10) !== site.attributes.activeDeploy &&
            deploy.attributes.state === 'completed' && site.attributes.wpState !== 'deploying' &&
            <div className="Deploys__item__cell Deploys__item__cell--actions">
              {site.attributes.wpState !== 'paused' ?
                <Tooltip label="Switch WordPress OFF to restore this deploy">
                  <OperationButton
                    className="button button--tiny"
                    disabled
                    label={[
                      <i className="icon--restore" />,
                      <span>Restore this deploy</span>,
                    ]}
                  />
                </Tooltip>
                :
                <Link
                  key={deploy.id}
                  to={`/site/${site.id}/deploys/${deploy.id}`}
                >
                  <OperationButton
                    onClick={() => this.handleRestoreDeploy(deploy.id)}
                    className="button button--tiny"
                    label={[
                      <i className="icon--restore" />,
                      <span>Restore this deploy</span>,
                    ]}
                  />
                </Link>
              }
            </div>
        }
      </div>
    );
  }

  render() {
    const { site } = this.props;

    return (
      <PaginationProvider params={this.state} site={site}>
        { ({ deploys, status }) => (
          <div>
            <div className="Deploys__bar">
              <div className="Deploys__bar__left">
                Create a static snapshot of your WordPress site
                and publish it online through our CDN
                {' '}
                <Tooltip label="Click for help">
                  <a href="https://www.hardypress.com/guides/understanding-deploys/" target="_blank" rel="noopener noreferrer">
                    <i className="icon--question-circle" />
                  </a>
                </Tooltip>
              </div>
              <div className="Deploys__bar__right">
                {
                  site.attributes.wpState === 'running' || site.attributes.wpState === 'deploying' ?
                    <OperationButton
                      onClick={this.handleDeploy}
                      className="button button--primary"
                      disabled={site.attributes.wpState === 'deploying'}
                      label={['Deploy site']}
                    />
                    :
                    <Tooltip label="Please switch WordPress ON">
                      <OperationButton
                        onClick={this.handleDeploy}
                        className="button button--primary"
                        disabled
                        label={['Deploy site']}
                      />
                    </Tooltip>
                }
              </div>
            </div>
            { deploys && deploys.length > 0 &&
            <div>
              <div className="Deploys__list">
                <div className="Deploys__item Deploys__item__header">
                  <div className="Deploys__item__cell Deploys__item__cell--state">
                    State
                  </div>
                  <div className="Deploys__item__cell Deploys__item__cell--info" >
                    Info
                  </div>
                  <div className="Deploys__item__cell Deploys__item__cell--download" >
                    Download
                  </div>
                  <div className="Deploys__item__cell Deploys__item__cell--actions" />
                </div>
                { deploys.map(this.renderDeploy) }
              </div>
              <Pagination
                data={status.deploys.meta.pagination}
                handlePageChange={this.handlePageChange}
              />
            </div>
            }
          </div>
        )}
      </PaginationProvider>
    );
  }
}



// WEBPACK FOOTER //
// ./app/components/Deploys.jsx